import { Drawer } from "antd";
import type { DrawerProps } from "antd";
import type { CSSProperties, ReactNode } from "react";
import { CommonSpin } from "../spin/CommonSpin";

type CommonDrawerProps = {
  open: boolean;
  title?: ReactNode;
  extra?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  loading?: boolean;
  width?: DrawerProps["width"];
  height?: DrawerProps["height"];
  placement?: DrawerProps["placement"];
  onClose?: () => void;
  closable?: boolean;
  maskClosable?: boolean;
  keyboard?: boolean;
  hideHeader?: boolean;
  hideFooter?: boolean;
  destroyOnClose?: boolean;
  bodyPadding?: number | string;
  bodyOverflow?: CSSProperties["overflow"];
  footerPadding?: number | string;
  styles?: DrawerProps["styles"];
  className?: DrawerProps["className"];
  rootClassName?: DrawerProps["rootClassName"];
  closeIcon?: ReactNode;

  drawerProps?: Omit<
    DrawerProps,
    | "open"
    | "title"
    | "extra"
    | "footer"
    | "children"
    | "onClose"
    | "closable"
    | "maskClosable"
    | "keyboard"
    | "width"
    | "height"
    | "placement"
    | "destroyOnClose"
    | "styles"
    | "className"
    | "rootClassName"
  >;
};

export const CommonDrawer = ({
  open,
  title,
  extra,
  footer,
  children,
  loading = false,
  width = "100%",
  height,
  placement = "right",
  onClose,
  closable = true,
  maskClosable = false,
  keyboard = false,
  hideHeader = false,
  hideFooter = false,
  destroyOnClose = false,
  bodyPadding,
  bodyOverflow,
  footerPadding,
  styles,
  className,
  rootClassName,
  closeIcon,
  drawerProps,
}: CommonDrawerProps) => {
  return (
    <Drawer
      {...drawerProps}
      open={open}
      title={hideHeader ? null : title}
      extra={hideHeader ? null : extra}
      footer={hideFooter ? null : footer}
      width={width}
      height={height}
      placement={placement}
      onClose={onClose}
      closable={!hideHeader && closable}
      maskClosable={maskClosable}
      keyboard={keyboard}
      destroyOnClose={destroyOnClose}
      className={className}
      rootClassName={rootClassName}
      closeIcon={closeIcon}
      styles={{
        ...styles,
        body: {
          padding: bodyPadding,
          overflow: bodyOverflow ?? "auto",
          height: "100%",
          minHeight: 0,
          ...styles?.body,
        },
        footer: {
          padding: footerPadding,
          ...styles?.footer,
        },
      }}
    >
      <CommonSpin
        spinning={loading}
        wrapperClassName="flex h-full min-h-0 min-w-0 flex-1 overflow-auto [&_.ant-spin-container]:flex [&_.ant-spin-container]:h-full [&_.ant-spin-container]:min-h-0 [&_.ant-spin-container]:min-w-0 [&_.ant-spin-container]:flex-1 [&_.ant-spin-container]:flex-col [&_.ant-spin-container]:overflow-auto"
      >
        {children}
      </CommonSpin>
    </Drawer>
  );
};
